import { useEffect, useState } from 'react';
import { Timestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadImage } from '../../../services/uploadImage.service';
import DOMPurify from 'dompurify';
import { WorkshopTemplate } from '../../../interfaces/template.interface';
import { WorkshopType } from '../../../interfaces/workshop.interface';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';

interface Props {
  template: WorkshopTemplate;
  onClose: () => void;
  onUpdated: () => void;
}

const sanitizeToString = (html: string): string => {
  return DOMPurify.sanitize(html, {
    RETURN_TRUSTED_TYPE: false,
    ALLOWED_TAGS: ['p', 'ul', 'li', 'strong', 'b', 'u', 'br', 'span'],
    ALLOWED_ATTR: [],
  }) as string;
};

const EditTemplatePopup = ({ template, onClose, onUpdated }: Props) => {
  const [title, setTitle] = useState(template.title);
  const [description, setDescription] = useState(template.description);
  const [type, setType] = useState<WorkshopType>(template.type);
  const [duration, setDuration] = useState(template.duration);
  const [location, setLocation] = useState(template.location);
  const [maxParticipants, setMaxParticipants] = useState(template.maxParticipants);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(template.imageUrl || null);
  const [uploading, setUploading] = useState(false);
  const [openDescEditor, setOpenDescEditor] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        orderedList: false,
        italic: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        strike: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: description || '<p></p>',
    editorProps: {
      attributes: { class: 'max-w-none focus:outline-none min-h-[420px] p-3' },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          if (event.shiftKey)
            editor?.chain().focus().liftListItem('listItem').run();
          else editor?.chain().focus().sinkListItem('listItem').run();
          return true;
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (openDescEditor && editor) {
      editor.commands.setContent(description || '<p></p>', {
        emitUpdate: false,
      });
    }
  }, [openDescEditor, editor, description]);

  const saveEditor = () => {
    const html = editor?.getHTML() ?? '';
    const clean = sanitizeToString(html);
    setDescription(clean);
    setOpenDescEditor(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploading(true);
    try {
      let imageUrl = template.imageUrl;
      if (imageFile) {
        const path = `templates/images/${Date.now()}-${imageFile.name}`;
        imageUrl = await uploadImage(imageFile, path);
      }

      const safeDescription = sanitizeToString(description ?? '');

      const docRef = doc(db, 'workshop_templates', template.id);
      await updateDoc(docRef, {
        title,
        description: safeDescription,
        type,
        duration,
        location,
        maxParticipants,
        imageUrl,
        updatedAt: Timestamp.now(),
      });

      onUpdated();
    } catch (error) {
      console.error('Erreur modification template :', error);
    } finally {
      setUploading(false);
    }
  };

  const Button = ({ onClick, active, children }: any) => (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded text-sm border ${
        active ? 'bg-[#623413] text-white' : 'bg-white text-gray-700'
      } hover:opacity-90`}
    >
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-scroll">
      <div className="max-h-[80vh] rounded-lg overflow-y-auto">
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 rounded-lg shadow-lg w-[800px] grid grid-cols-2 gap-4"
        >
          <h2 className="text-xl font-semibold col-span-2">Modifier le template</h2>

          <div className="col-span-2">
            <label className="text-sm text-gray-600">Titre</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border px-3 py-2 rounded w-full"
              required
            />
          </div>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-600">
                Description (aperçu)
              </label>
              <button
                type="button"
                onClick={() => setOpenDescEditor(true)}
                className="text-sm bg-[#623413] text-white px-3 py-1.5 rounded hover:opacity-90"
              >
                Ouvrir l'éditeur
              </button>
            </div>

            <div
              className="prose prose-neutral max-w-none border rounded p-3 min-h-[96px] bg-gray-50 max-h-36 overflow-y-auto"
              dangerouslySetInnerHTML={{
                __html: description?.trim()
                  ? sanitizeToString(description)
                  : '<p class="text-gray-400">Aucune description. Cliquez sur "Ouvrir l\'éditeur".</p>',
              }}
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as WorkshopType)}
              className="border px-3 py-2 rounded w-full"
            >
              <option value="public">Tout public</option>
              <option value="health">Santé</option>
              <option value="teambuilding">Teambuilding</option>
            </select>
          </div>

          <div>
            <label className="text-sm text-gray-600">Durée (min)</label>
            <input
              type="number"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="border px-3 py-2 rounded w-full"
              required
              min="1"
            />
          </div>

          <div className="col-span-2">
            <label className="text-sm text-gray-600">Lieu</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="border px-3 py-2 rounded w-full"
              required
            />
          </div>

          <div>
            <label className="text-sm text-gray-600">Participants max</label>
            <input
              type="number"
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
              className="border px-3 py-2 rounded w-full"
              required
              min="1"
            />
          </div>

          <div className="col-span-2">
            <label className="text-sm text-gray-600">Modifier l'image (optionnelle)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setImageFile(file);
                if (file) setPreviewUrl(URL.createObjectURL(file));
              }}
              className="border px-3 py-2 rounded w-full"
            />
          </div>

          {previewUrl && (
            <div className="col-span-2">
              <img
                src={previewUrl}
                alt="Aperçu"
                className="h-32 w-auto object-cover rounded border"
              />
            </div>
          )}

          <div className="col-span-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500"
              disabled={uploading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="bg-[#623413] text-white px-4 py-2 rounded hover:opacity-90"
              disabled={uploading}
            >
              {uploading ? 'Mise à jour...' : 'Mettre à jour'}
            </button>
          </div>
        </form>
      </div>

      {openDescEditor && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="bg-white w-[1000px] max-w-[95vw] max-h-[85vh] rounded-lg shadow-xl flex flex-col">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold">Éditeur de description</h3>
              <button
                onClick={() => setOpenDescEditor(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Fermer
              </button>
            </div>

            <div className="px-5 py-2 border-b flex flex-wrap gap-2">
              <Button
                onClick={() => editor?.chain().focus().toggleBold().run()}
                active={editor?.isActive('bold')}
              >
                Gras
              </Button>
              <Button
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
                active={editor?.isActive('underline')}
              >
                Souligné
              </Button>
              <Button
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                active={editor?.isActive('bulletList')}
              >
                • Liste
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().liftListItem('listItem').run()
                }
              >
                ← Indent-
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().sinkListItem('listItem').run()
                }
              >
                → Indent+
              </Button>
              <Button
                onClick={() =>
                  editor?.chain().focus().unsetAllMarks().clearNodes().run()
                }
              >
                Nettoyer
              </Button>
            </div>

            <div className="p-5 overflow-auto">
              <div className="border rounded">
                <EditorContent editor={editor} />
              </div>
              <style>
                {`
                  .ProseMirror ul { list-style: disc; padding-left: 1.25rem; }
                  .ProseMirror li { margin: 0.25rem 0; }
                `}
              </style>
            </div>

            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button
                onClick={() => setOpenDescEditor(false)}
                className="px-4 py-2 text-sm rounded border"
              >
                Annuler
              </button>
              <button
                onClick={saveEditor}
                className="px-4 py-2 text-sm rounded bg-[#623413] text-white"
              >
                Enregistrer la description
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditTemplatePopup;
